import { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import CopyrightIcon from '@mui/icons-material/Copyright';
import { CircularProgress, Fade, Tooltip, Chip, LinearProgress } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';

const UserCreate = () => {
    // Session validation
    useSessionValidation();
    
    const history = useHistory();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [formProgress, setFormProgress] = useState(0);

    useEffect(() => {
        setMounted(true);
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }

    }, [isAuthenticated, companyId]);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [userName, setUserName] = useState("");
    const [contactNumber, setContactNumber] = useState("");
    const [email, setEmail] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const generatedUserName = `${firstName.toLowerCase()}_${lastName.toLowerCase()}`;
        // Limit the username to a maximum length of 15 characters
        const limitedUserName = generatedUserName.slice(0, 13);
        setUserName(limitedUserName);
        calculateProgress();
    }, [firstName, lastName, contactNumber, email]);

    const calculateProgress = () => {
        const requiredFields = [firstName, lastName, contactNumber, email];
        const filledFields = requiredFields.filter(field => field && field.trim() !== '').length;
        const progress = (filledFields / requiredFields.length) * 100;
        setFormProgress(Math.round(progress));
    };

    const clearForm = () => {
        setFirstName("");
        setLastName("");
        setUserName("");
        setContactNumber("");
        setEmail("");
    };

    const submitData = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        if (!firstName) {
            setErrorMessage('First Name is required !');
            setIsLoading(false);
            return;
        }

        if (!lastName) {
            setErrorMessage('Last Name is required !');
            setIsLoading(false);
            return;
        }

        if (!contactNumber) {
            setErrorMessage('Contact Number is required !');
            setIsLoading(false);
            return;
        }

        if (!email) {
            setErrorMessage('Email is required !');
            setIsLoading(false);
            return;
        }

        const CreateData = {
            FirstName: firstName,
            LastName: lastName,
            UserName: userName,
            Email: email,
            ContactNumber: contactNumber,
            CreatedBy: userId,
            CompanyId: companyId,
            StaffId: userId,
            CreatedDate: new Date(),
        };

        postData('UserApi', CreateData, token)
            .then((response) => {
                console.log('User Created success:', response);
                setSuccessMessage("User Created successfully!");
                setErrorMessage("");
                clearForm();
                history.push('/UserIndex');
                setTimeout(() => {
                    setSuccessMessage("");
                }, 3000);
            })
            .catch((error) => {
                console.error('User Create error:', error);
                setIsLoading(false);
            });
    };

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{
                minWidth: '100%',
                background: '#f5f7fa',
                padding: '10px',
                position: 'relative'
            }}>
                <style>{`
                    @keyframes slideIn {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .input-field {
                        transition: all 0.3s ease;
                        border: 2px solid transparent;
                    }
                    .input-field:focus {
                        border-color: #667eea;
                        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                        transform: translateY(-2px);
                    }
                    .input-field:hover {
                        border-color: #e0e0e0;
                    }
                    .action-button {
                        transition: all 0.3s ease;
                        text-transform: none;
                        font-weight: 600;
                        letter-spacing: 0.5px;
                    }
                    .action-button:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
                    }
                    .section-card {
                        animation: slideIn 0.6s ease-out forwards;
                        opacity: 0;
                    }
                    .section-card:nth-child(1) { animation-delay: 0.1s; }
                    .section-card:nth-child(2) { animation-delay: 0.2s; }
                    @keyframes shimmer {
                        0% { background-position: -1000px 0; }
                        100% { background-position: 1000px 0; }
                    }
                    .shimmer {
                        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
                        background-size: 1000px 100%;
                        animation: shimmer 2s infinite;
                    }
                `}</style>

                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    padding: '12px',
                    position: 'relative'
                }}>
                    {/* Progress Bar */}
                    <LinearProgress
                        variant="determinate"
                        value={formProgress}
                        style={{
                            height: '3px',
                            borderRadius: '4px 4px 0 0',
                            background: '#e0e0e0',
                            marginBottom: '10px'
                        }}
                        sx={{
                            '& .MuiLinearProgress-bar': {
                                background: formProgress === 100
                                    ? 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)'
                                    : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                                transition: 'all 0.5s ease'
                            }
                        }}
                    />

                    {/* Header Section */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                padding: '8px',
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 8px rgba(102, 126, 234, 0.3)'
                            }}>
                                <PersonAddIcon style={{ fontSize: '22px', color: 'white' }} />
                            </div>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <h1 style={{
                                        fontSize: '20px',
                                        fontWeight: '700',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        margin: 0,
                                        letterSpacing: '-0.3px'
                                    }}>
                                        Create New User
                                    </h1>
                                    <Tooltip title="Complete all required fields marked with *" arrow placement="right">
                                        <InfoIcon style={{ fontSize: '16px', color: '#667eea', cursor: 'pointer' }} />
                                    </Tooltip>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                                    <p style={{ margin: 0, color: '#666', fontSize: '11px' }}>
                                        Add a new user to the system
                                    </p>
                                    <Chip
                                        icon={<CheckCircleIcon style={{ fontSize: '14px' }} />}
                                        label={`${formProgress}% Complete`}
                                        size="small"
                                        style={{
                                            height: '20px',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            background: formProgress === 100 ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Tooltip title={formProgress < 100 ? "Complete required fields to save" : "Save new user"} arrow>
                                <span>
                                    <Button
                                        variant="contained"
                                        onClick={submitData}
                                        disabled={isLoading}
                                        startIcon={isLoading ? null : <SaveIcon />}
                                        className="action-button"
                                        style={{
                                            background: isLoading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            padding: '6px 16px',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            boxShadow: isLoading ? 'none' : '0 2px 8px rgba(102, 126, 234, 0.3)',
                                            border: 'none',
                                            minHeight: '32px',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {isLoading && <div className="shimmer" style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            zIndex: 1
                                        }} />}
                                        <span style={{ position: 'relative', zIndex: 2 }}>
                                            {isLoading ? <CircularProgress size={16} color="inherit" /> : "Save User"}
                                        </span>
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Return to user list" arrow>
                                <Button
                                    variant="outlined"
                                    component={Link}
                                    to="/UserIndex"
                                    startIcon={<ArrowBackIcon />}
                                    className="action-button"
                                    style={{
                                        borderColor: '#667eea',
                                        color: '#667eea',
                                        padding: '6px 16px',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        borderWidth: '1.5px',
                                        minHeight: '32px'
                                    }}
                                >
                                    Back to List
                                </Button>
                            </Tooltip>
                        </div>
                    </div>

                    {/* Error/Success Messages */}
                    {errorMessage && (
                        <div style={{
                            padding: '10px',
                            marginBottom: '10px',
                            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                            color: 'white',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '500'
                        }}>
                            {errorMessage}
                        </div>
                    )}
                    {successMessage && (
                        <div style={{
                            padding: '10px',
                            marginBottom: '10px',
                            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                            color: 'white',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: '500'
                        }}>
                            {successMessage}
                        </div>
                    )}

                    {/* Personal Information Section */}
                    <div className="section-card" style={{
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                        padding: '10px',
                        borderRadius: '10px',
                        marginBottom: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {firstName && lastName && (
                            <div style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                background: 'rgba(17, 153, 142, 0.9)',
                                borderRadius: '50%',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <CheckCircleIcon style={{ fontSize: '16px', color: 'white' }} />
                            </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <PersonIcon style={{ color: '#667eea', fontSize: '18px' }} />
                            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                                Personal Information
                            </h3>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                            <div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: '#4a5568',
                                    marginBottom: '4px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                }}>
                                    <PersonIcon style={{ fontSize: '13px' }} />
                                    First Name *
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        className="input-field"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        type="text"
                                        placeholder="Enter first name"
                                        maxLength={30}
                                        style={{
                                            width: '100%',
                                            padding: '6px 10px',
                                            fontSize: '13px',
                                            borderRadius: '8px',
                                            border: firstName && firstName.trim() !== '' ? '1.5px solid #11998e' : '1.5px solid #e2e8f0',
                                            background: 'white',
                                            outline: 'none',
                                            fontWeight: '500'
                                        }}
                                    />
                                    {firstName && firstName.trim() !== '' && (
                                        <CheckCircleIcon style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            fontSize: '16px',
                                            color: '#11998e'
                                        }} />
                                    )}
                                </div>
                            </div>
                            <div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: '#4a5568',
                                    marginBottom: '4px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                }}>
                                    <PersonIcon style={{ fontSize: '13px' }} />
                                    Last Name *
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        className="input-field"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        type="text"
                                        placeholder="Enter last name"
                                        maxLength={30}
                                        style={{
                                            width: '100%',
                                            padding: '6px 10px',
                                            fontSize: '13px',
                                            borderRadius: '8px',
                                            border: lastName && lastName.trim() !== '' ? '1.5px solid #11998e' : '1.5px solid #e2e8f0',
                                            background: 'white',
                                            outline: 'none',
                                            fontWeight: '500'
                                        }}
                                    />
                                    {lastName && lastName.trim() !== '' && (
                                        <CheckCircleIcon style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            fontSize: '16px',
                                            color: '#11998e'
                                        }} />
                                    )}
                                </div>
                            </div>
                            <div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: '#4a5568',
                                    marginBottom: '4px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                }}>
                                    <AccountCircleIcon style={{ fontSize: '13px' }} />
                                    User Name (Auto-generated)
                                </label>
                                <input
                                    className="input-field"
                                    value={userName}
                                    type="text"
                                    placeholder="Auto-generated"
                                    readOnly
                                    style={{
                                        width: '100%',
                                        padding: '6px 10px',
                                        fontSize: '13px',
                                        borderRadius: '8px',
                                        border: '1.5px solid #e2e8f0',
                                        background: '#fafafa',
                                        outline: 'none',
                                        cursor: 'not-allowed'
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Contact Information Section */}
                    <div className="section-card" style={{
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                        padding: '10px',
                        borderRadius: '10px',
                        marginBottom: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {contactNumber && email && (
                            <div style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                background: 'rgba(17, 153, 142, 0.9)',
                                borderRadius: '50%',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <CheckCircleIcon style={{ fontSize: '16px', color: 'white' }} />
                            </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <PhoneIcon style={{ color: '#667eea', fontSize: '18px' }} />
                            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                                Contact Details
                            </h3>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                            <div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: '#4a5568',
                                    marginBottom: '4px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                }}>
                                    <PhoneIcon style={{ fontSize: '13px' }} />
                                    Contact Number *
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        className="input-field"
                                        value={contactNumber}
                                        onChange={(e) => setContactNumber(e.target.value)}
                                        type="text"
                                        placeholder="Enter contact number"
                                        maxLength={15}
                                        style={{
                                            width: '100%',
                                            padding: '6px 10px',
                                            fontSize: '13px',
                                            borderRadius: '8px',
                                            border: contactNumber && contactNumber.trim() !== '' ? '1.5px solid #11998e' : '1.5px solid #e2e8f0',
                                            background: 'white',
                                            outline: 'none',
                                            fontWeight: '500'
                                        }}
                                    />
                                    {contactNumber && contactNumber.trim() !== '' && (
                                        <CheckCircleIcon style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            fontSize: '16px',
                                            color: '#11998e'
                                        }} />
                                    )}
                                </div>
                            </div>
                            <div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    color: '#4a5568',
                                    marginBottom: '4px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                }}>
                                    <EmailIcon style={{ fontSize: '13px' }} />
                                    Email Address *
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        className="input-field"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        type="email"
                                        placeholder="user@example.com"
                                        maxLength={40}
                                        style={{
                                            width: '100%',
                                            padding: '6px 10px',
                                            fontSize: '13px',
                                            borderRadius: '8px',
                                            border: email && email.trim() !== '' ? '1.5px solid #11998e' : '1.5px solid #e2e8f0',
                                            background: 'white',
                                            outline: 'none',
                                            fontWeight: '500'
                                        }}
                                    />
                                    {email && email.trim() !== '' && (
                                        <CheckCircleIcon style={{
                                            position: 'absolute',
                                            right: '10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            fontSize: '16px',
                                            color: '#11998e'
                                        }} />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    fontFamily: 'serif',
                    fontWeight: '600',
                    color: '#2c3e50',
                    fontSize: '13px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    zIndex: 1000
                }}>
                    <CopyrightIcon style={{ fontSize: '18px', color: '#667eea' }} />
                    <span>EFFTABS SOLUTIONS LLP</span>
                </div>
            </div>
        </Fade>
    );
}

export default UserCreate;

